import { CheckRounded, ExpandMore } from '@mui/icons-material'
import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import { styled } from '@mui/material/styles'
import clsx from 'clsx'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ButtonHeader from '../../../../shared/styles/ButtonHeader'

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.grey[700]
  }
}))

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 20,
  '& > div': {
    background: theme.palette.primary.main
  },
  '& .MuiMenuItem-root': {
    color: theme.palette.grey[50],
    padding: '4px 24px',
    fontSize: 16
  }
}))

const CheckIcon = styled(CheckRounded)(({ theme }) => ({
  display: 'none',
  '&.active': {
    backgroundColor: 'transparent',
    display: 'block',
    color: theme.palette.grey[50],
    marginLeft: 8
  },
  '&.admin': {
    marginRight: 8,
    marginLeft: 0
  },
  [`${theme.breakpoints.down('sm')}`]: {
    '&.active': {
      color: theme.palette.success.main
    }
  }
}))

const StyledButton = styled(ButtonHeader)(({ theme }) => ({
  '& svg': {
    color: `${theme.palette.grey[50]} !important`
  }
}))

interface TypeFilterProps {
  id: number
  type: string
  categories: Array<{
    id: number
    name: string
  }>
  active: boolean
  handleChangeFilter: (id: number, type: number, categoryId: number | null, label: string) => void
  admin?: boolean
  filterCategoryId?: number | null
}

function TypeFilter({
  id,
  type,
  categories,
  active,
  handleChangeFilter,
  admin = false,
  filterCategoryId
}: TypeFilterProps) {
  const { t } = useTranslation(['global'])
  const [openMenu, setOpenMenu] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const prevOpen = useRef(openMenu)
  useEffect(() => {
    if (prevOpen.current === true && openMenu === false) {
      anchorRef.current?.focus()
    }
    prevOpen.current = openMenu
  }, [openMenu])

  const handleItemSelected = (id: number | null) => {
    switch (id) {
      case 1:
        handleChangeFilter(id, 1, null, t('slider'))
        break
      case 2:
        handleChangeFilter(id, 2, null, t('link'))
        break
      case 3:
        handleChangeFilter(id, 2, 1, `${t('link')} ${t('top')}`)
        break
      case 4:
        handleChangeFilter(id, 2, 2, `${t('link')} ${t('leftBottom')}`)
        break
      case 5:
        handleChangeFilter(id, 2, 3, `${t('link')} ${t('rightBottom')}`)
        break
      case 6:
        handleChangeFilter(id, 3, 1, `${t('internal')}`)
        break
      default:
        handleChangeFilter(1, 1, null, t('create-slider'))
        break
    }
    setOpenMenu(false)
  }

  const handleToggle = () => {
    if (categories.length > 0 || admin) {
      setOpenMenu(prevOpen => !prevOpen)
    } else {
      handleItemSelected(id)
    }
  }

  const handleClose = (event: Event | TouchEvent) => {
    if (anchorRef.current && event.target instanceof Node && anchorRef.current.contains(event.target)) {
      return
    }
    setOpenMenu(false)
  }

  const handleListKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpenMenu(false)
    }
  }

  return (
    <>
      <StyledButton
        ref={anchorRef}
        aria-controls={openMenu ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        color="primary"
        className={clsx({ active: active })}
        endIcon={admin || categories.length > 0 ? <ExpandMore color="inherit" /> : undefined}
        onClick={handleToggle}
      >
        {type}
      </StyledButton>

      {(admin || categories.length > 0) && (
        <StyledPopper open={openMenu} anchorEl={anchorRef.current} role="menu" transition disablePortal>
          {({ TransitionProps, placement }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: placement === 'bottom' ? 'left top' : 'left bottom' }}>
              <Paper elevation={0} square>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList autoFocusItem={openMenu} id="menu-list-grow" onKeyDown={handleListKeyDown}>
                    {categories.map(category => {
                      const activeCategory = !admin && filterCategoryId !== null && category.id === filterCategoryId

                      return (
                        <StyledMenuItem key={category.id} onClick={() => handleItemSelected(category.id)}>
                          {category.name}
                          {!admin && <CheckIcon className={clsx(activeCategory && 'active')} />}
                        </StyledMenuItem>
                      )
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </StyledPopper>
      )}
    </>
  )
}

export default TypeFilter
